import React, { FC, useRef } from 'react';
import type { UploadProps } from 'antd';
import { notification, Upload } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import type { RcFile } from 'antd/lib/upload';
import './fileUploader.scss';
import { FileIcon, FileUploadIcon, CancelIcon } from 'components/Elements/CustomSVGIcon';

/**
 * =============================================================================
 * SECURITY NOTICE
 * =============================================================================
 *
 * This component performs CLIENT-SIDE validation only. Client-side validation
 * is for user experience (immediate feedback) and is NOT a security control.
 *
 * IMPORTANT: All file uploads MUST be validated server-side. The backend
 * performs authoritative validation including:
 * - File extension whitelist enforcement
 * - MIME type verification (with magic byte detection where applicable)
 * - File size limits
 * - File count limits
 * - Malware scanning (if configured)
 *
 * Client-side validation can be bypassed by:
 * - Disabling JavaScript
 * - Modifying requests directly (curl, Postman, etc.)
 * - Browser developer tools
 * - Renaming files to spoof extensions
 *
 * The server will reject any files that don't pass validation, regardless of
 * what the client sends. See: VisitRequestReviewController::_validateFileUploads()
 * =============================================================================
 */

/**
 * Maximum file size in bytes (10MB)
 */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Allowed file extensions (lowercase).
 * Must match server-side ALLOWED_UPLOAD_MIMES constant.
 */
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'txt'];

/**
 * MIME types for the accept attribute (browser file picker restriction).
 * This provides better UX by filtering the file picker dialog.
 */
const ACCEPT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
].join(',');

/**
 * Mapping of extensions to their expected MIME types for client-side validation.
 * Note: This is advisory only - server performs authoritative MIME validation.
 */
const EXTENSION_MIME_MAP: Record<string, string[]> = {
    pdf: ['application/pdf'],
    doc: ['application/msword'],
    docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    xls: ['application/vnd.ms-excel'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    jpg: ['image/jpeg'],
    jpeg: ['image/jpeg'],
    png: ['image/png'],
    gif: ['image/gif'],
    txt: ['text/plain'],
};

interface BaseFileUploaderProps {
    /**
     * Callback fired when the file list changes.
     * Required for controlled component behavior.
     */
    onChange: (fileList: UploadFile[]) => void;
    /**
     * Maximum number of files allowed
     * @default 20
     */
    maxFiles?: number;
    /**
     * Controlled file list. When provided, the component operates in controlled mode.
     * The parent component is responsible for managing the file list state.
     */
    // eslint-disable-next-line react/require-default-props
    fileList?: UploadFile[];
    /**
     * Maximum file size in bytes
     * @default 10485760 (10MB)
     */
    maxFileSizeBytes?: number;
}

type FileUploaderProps =
    | (BaseFileUploaderProps & { showFileList: true; onRemove: (uid: string) => void })
    | (BaseFileUploaderProps & { showFileList?: false; onRemove?: never });

interface DraggableUploadListItemProps {
    originNode: React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>;
}

const DraggableUploadListItem = ({ originNode }:DraggableUploadListItemProps) => (
    <div className="file-list">
        <FileIcon />
        {originNode}
        <div className="border" />
    </div>
);

/**
 * Get file extension from filename (lowercase)
 */
const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    if (parts.length <= 1) return '';
    const ext = parts[parts.length - 1];
    return ext ? ext.toLowerCase() : '';
};

/**
 * Validate file extension against allowed list
 */
const isValidExtension = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return ALLOWED_EXTENSIONS.includes(ext);
};

/**
 * Check if MIME type is suspicious (doesn't match expected type for extension).
 * Note: Client-side MIME detection is unreliable - browsers may report different
 * MIME types or empty strings. This is used for warnings only; server-side
 * validation is the primary security control.
 *
 * @returns 'valid' if MIME matches, 'suspicious' if mismatch, 'unknown' if can't determine
 */
const checkMimeType = (filename: string, mimeType: string): 'valid' | 'suspicious' | 'unknown' => {
    // Empty or missing MIME type - can't validate, let server handle it
    if (!mimeType || mimeType === '' || mimeType === 'application/octet-stream') {
        return 'unknown';
    }

    const ext = getFileExtension(filename);
    const allowedMimes = EXTENSION_MIME_MAP[ext];

    // Unknown extension - shouldn't happen if extension validation passed
    if (!allowedMimes) return 'unknown';

    return allowedMimes.includes(mimeType) ? 'valid' : 'suspicious';
};

/**
 * Validation result for a file
 */
interface FileValidationResult {
    file: RcFile;
    isValid: boolean;
    error?: 'extension' | 'size' | 'limit';
    warning?: 'mime';
}

/**
 * Validate all files in a batch and return results
 */
const validateFileBatch = (
    files: RcFile[],
    maxSizeBytes: number,
    availableSlots: number,
): FileValidationResult[] => {
    let validCount = 0;

    return files.map((file): FileValidationResult => {
        // Check extension
        if (!isValidExtension(file.name)) {
            return { file, isValid: false, error: 'extension' };
        }

        // Check size
        if (file.size > maxSizeBytes) {
            return { file, isValid: false, error: 'size' };
        }

        // Check limit
        if (validCount >= availableSlots) {
            return { file, isValid: false, error: 'limit' };
        }

        // Check MIME type (warning only)
        const mimeCheck = checkMimeType(file.name, file.type);

        validCount += 1;

        if (mimeCheck === 'suspicious') {
            return { file, isValid: true, warning: 'mime' };
        }
        return { file, isValid: true };
    });
};

/**
 * Show batched notifications for validation errors
 */
const showBatchedNotifications = (
    results: FileValidationResult[],
    maxSizeBytes: number,
    maxFiles: number,
    currentFileCount: number,
): void => {
    const extensionErrors = results.filter((r) => r.error === 'extension');
    const sizeErrors = results.filter((r) => r.error === 'size');
    const limitErrors = results.filter((r) => r.error === 'limit');
    const mimeWarnings = results.filter((r) => r.warning === 'mime');

    // Show single notification for extension errors
    if (extensionErrors.length > 0) {
        const fileNames = extensionErrors.map((r) => r.file.name);
        const displayNames = fileNames.length <= 3
            ? fileNames.join(', ')
            : `${fileNames.slice(0, 3).join(', ')} and ${fileNames.length - 3} more`;

        notification.error({
            message: 'Unsupported File Type',
            description: extensionErrors.length === 1
                ? `The file "${fileNames[0]}" has an unsupported extension. Allowed: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}.`
                : `${extensionErrors.length} files have unsupported extensions (${displayNames}). Allowed: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}.`,
        });
    }

    // Show single notification for size errors
    if (sizeErrors.length > 0) {
        const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024));
        const fileNames = sizeErrors.map((r) => r.file.name);
        const displayNames = fileNames.length <= 3
            ? fileNames.join(', ')
            : `${fileNames.slice(0, 3).join(', ')} and ${fileNames.length - 3} more`;

        notification.error({
            message: 'File Too Large',
            description: sizeErrors.length === 1
                ? `The file "${fileNames[0]}" exceeds the maximum size of ${maxSizeMB}MB.`
                : `${sizeErrors.length} files exceed the maximum size of ${maxSizeMB}MB (${displayNames}).`,
        });
    }

    // Show single notification for limit errors
    if (limitErrors.length > 0) {
        const acceptedCount = results.filter((r) => r.isValid).length;
        notification.error({
            message: 'Too Many Files',
            description: `Maximum ${maxFiles} files allowed. `
                + `You have ${currentFileCount} file(s). `
                + `Accepted ${acceptedCount}, rejected ${limitErrors.length} file(s).`,
        });
    }

    // Show single warning for MIME type issues
    if (mimeWarnings.length > 0) {
        const fileNames = mimeWarnings.map((r) => r.file.name);
        const displayNames = fileNames.length <= 3
            ? fileNames.join(', ')
            : `${fileNames.slice(0, 3).join(', ')} and ${fileNames.length - 3} more`;

        // Log warnings for debugging
        mimeWarnings.forEach((r) => {
            // eslint-disable-next-line no-console
            console.warn(
                `File "${r.file.name}" has unexpected MIME type "${r.file.type}". Server will validate.`,
            );
        });

        notification.warning({
            message: 'Files May Be Invalid',
            description: mimeWarnings.length === 1
                ? `The file "${fileNames[0]}" may have been renamed. It will be validated on upload.`
                : `${mimeWarnings.length} files may have been renamed (${displayNames}). They will be validated on upload.`,
        });
    }
};

const FileUploader: FC<FileUploaderProps> = ({
    onChange,
    fileList,
    maxFiles = 20,
    maxFileSizeBytes = MAX_FILE_SIZE_BYTES,
    showFileList = false,
    onRemove,
}) => {
    const { Dragger } = Upload;

    // Track UIDs of files that passed beforeUpload validation
    // This is used to filter the fileList in onChange
    const validFileUidsRef = useRef<Set<string>>(new Set());

    // Track if we've already shown notifications for the current batch
    const batchProcessedRef = useRef<string | null>(null);

    // Get current file count for maxFiles enforcement
    const currentFileCount = fileList?.length ?? 0;
    const availableSlots = Math.max(0, maxFiles - currentFileCount);

    const props: UploadProps = {
        name: 'file',
        multiple: true,
        // Restrict file picker to allowed types (UX improvement)
        accept: ACCEPT_MIME_TYPES,
        // Controlled mode when fileList is provided
        ...(fileList !== undefined && { fileList }),
        beforeUpload: (file, uploadFileList) => {
            // Generate a batch ID to track notifications
            const batchId = uploadFileList.map((f) => f.uid).join('-');

            // Process entire batch only once (on first file)
            if (batchProcessedRef.current !== batchId) {
                batchProcessedRef.current = batchId;

                // Validate all files in the batch
                const results = validateFileBatch(
                    uploadFileList,
                    maxFileSizeBytes,
                    availableSlots,
                );

                // Update the set of valid file UIDs
                validFileUidsRef.current = new Set(
                    results.filter((r) => r.isValid).map((r) => r.file.uid),
                );

                // Show batched notifications
                showBatchedNotifications(results, maxFileSizeBytes, maxFiles, currentFileCount);
            }

            // Return LIST_IGNORE for invalid files, false for valid files
            if (validFileUidsRef.current.has(file.uid)) {
                return false; // Valid file - add to list but don't auto-upload
            }
            return Upload.LIST_IGNORE;
        },
        onChange(info) {
            // Reset batch tracking when file list changes
            batchProcessedRef.current = null;

            // Filter the fileList to only include files that passed validation
            // This handles the case where AntD still calls onChange for all files
            const validFiles = info.fileList.filter((f) => {
                // Keep existing files (already in the list before this batch)
                const existingUids = new Set(fileList?.map((ef) => ef.uid) ?? []);
                if (existingUids.has(f.uid)) return true;

                // Keep newly added files that passed validation
                return validFileUidsRef.current.has(f.uid);
            });

            onChange(validFiles);
        },
        showUploadList: false,
        // eslint-disable-next-line react/no-unstable-nested-components
        itemRender: (originNode) => (
            <DraggableUploadListItem originNode={originNode} />
        ),
    };

    return (
        <div className="file-dragger-container">
            <Dragger
                {...props}>
                <div className="file-uploader-content">
                    <div className="icon-wrapper">
                        <FileUploadIcon />
                    </div>
                    <p className="caption">Drag and drop or click to upload</p>
                    <p className="sub-caption">
                        Uploading documentation is optional
                    </p>
                </div>
            </Dragger>
            {showFileList && fileList && fileList.length > 0 && (
                <div className="uploaded-file-list">
                    {fileList.map((file) => (
                        <div className="uploaded-file-item" key={file.uid}>
                            <div className="file-info">
                                <FileIcon />
                                <span>{file.name}</span>
                            </div>
                            <button
                                type="button"
                                className="remove-file-button"
                                aria-label={`Remove ${file.name}`}
                                onClick={() => onRemove?.(file.uid)}>
                                <CancelIcon />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUploader;
